import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, TextAction } from '@payforge/ui';
import { formatDayMonthYear } from '../../lib/dates';
import { useMe } from '../../lib/useMe';
import { useReminderStatus } from '../../lib/useReminderStatus';
import type { OverduePeriodDto, PreDeadlineStatusDto } from '../../lib/types';

// Dismissal is keyed by the REAL user id (same rule as useTheme), so "View as" never overwrites
// the admin's own dismissal. The stored value is the dismissed pre-deadline periodStart: the
// banner returns by itself for the next period because its periodStart no longer matches.
const DISMISS_PREFIX = 'payforgehr.timesheet-reminder.dismissed.';

function dismissKey(realUserId: number | undefined): string | null {
  return realUserId != null ? DISMISS_PREFIX + realUserId : null;
}

function readDismissed(key: string | null): string | null {
  if (key === null) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode or storage disabled: the banner shows again, which errs on the safe side.
    return null;
  }
}

function writeDismissed(key: string | null, periodStart: string): void {
  if (key === null) return;
  try {
    localStorage.setItem(key, periodStart);
  } catch {
    // Non-fatal: the dismissal still applies for this session, it just won't survive a reload.
  }
}

// "A", "A and B", "A, B and C" - the labels arrive preformatted and oldest-first.
function joinLabels(periods: OverduePeriodDto[]): string {
  const labels = periods.map((p) => p.label);
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]!}`;
}

function overdueSentence(periods: OverduePeriodDto[]): string {
  const many = periods.length > 1;
  const labels = joinLabels(periods);
  // allSubmitted means the employee's part is done: submitted, with every workday holding its
  // hours. The emails stop there (a period with a short workday keeps them going until the hours
  // are logged or it is approved); the banner persists until approval, so a backlog that only
  // awaits approvers reads as a status note rather than a call to action.
  if (periods.every((p) => p.allSubmitted)) {
    return many
      ? `Your timesheets for ${labels} have been submitted and are awaiting approval.`
      : `Your timesheet for ${labels} has been submitted and is awaiting approval.`;
  }
  return many
    ? `Your timesheets for ${labels} are overdue - fill them in and submit them now.`
    : `Your timesheet for ${labels} is overdue - fill it in and submit it now.`;
}

/**
 * Home-page timesheet nudges, mirroring the reminder emails: a red banner for overdue periods
 * (never dismissable) above an amber one once a pre-deadline reminder has fired for the running
 * period (dismissable per period). Banners are a courtesy, never a blocker: while loading, on
 * error, and for the empty DTO (timesheets hidden, no employee record, or not full-time, since
 * reminders are for full-time employees only) nothing renders.
 */
export function TimesheetReminderBanners() {
  const me = useMe();
  const status = useReminderStatus();
  const [justDismissed, setJustDismissed] = useState<string | null>(null);

  const data = status.data;
  if (!data || data.currentPeriod == null) return null;

  const overdue = data.overduePeriods;
  const key = dismissKey(me.data?.realUserId);
  const dismissed = justDismissed ?? readDismissed(key);
  // The amber banner waits for the per-user key: rendering before /me resolves could flash a
  // reminder the user already dismissed.
  const preDeadline =
    key !== null && data.preDeadline !== null && data.preDeadline.periodStart !== dismissed
      ? data.preDeadline
      : null;
  // In "View as" the status is the impersonated employee's but the dismissal key is the ADMIN's
  // real one, so dismissing here would silently hide the admin's own banner. Show, don't dismiss.
  const dismissable = me.data?.impersonating !== true;
  if (overdue.length === 0 && preDeadline === null) return null;

  function dismissPreDeadline(periodStart: string) {
    writeDismissed(key, periodStart);
    setJustDismissed(periodStart);
  }

  return (
    <div className="mt-5 space-y-3">
      {overdue.length > 0 ? <OverdueBanner periods={overdue} /> : null}
      {preDeadline !== null ? (
        <PreDeadlineBanner
          preDeadline={preDeadline}
          onDismiss={dismissable ? dismissPreDeadline : null}
        />
      ) : null}
    </div>
  );
}

/**
 * The banners deliberately speak the home page's own card language - neutral surface, hairline
 * edge, muted body text - with the status colour confined to a 2px accent stripe, so a nudge
 * reads as part of the UI instead of a legacy error box.
 */
function NoticeCard({
  accent,
  testId,
  title,
  sentence,
  actions,
}: {
  accent: 'danger' | 'warning';
  testId: string;
  title: string;
  sentence: string;
  actions: ReactNode;
}) {
  const stripe = accent === 'danger' ? 'border-l-danger' : 'border-l-warning';
  return (
    <Card padding="none" className="overflow-hidden" data-testid={testId}>
      <div
        className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-l-2 px-4 py-2.5 ${stripe}`}
      >
        <p className="min-w-0 text-sm text-fg-muted">
          <span className="font-semibold text-fg">{title}</span>
          <span className="mx-2" aria-hidden="true">
            &middot;
          </span>
          {sentence}
        </p>
        <div className="flex shrink-0 items-center gap-4">{actions}</div>
      </div>
    </Card>
  );
}

function OverdueBanner({ periods }: { periods: OverduePeriodDto[] }) {
  return (
    <NoticeCard
      accent="danger"
      testId="overdue-timesheets-banner"
      title="Overdue timesheets"
      sentence={overdueSentence(periods)}
      actions={
        <Link
          to={`/timesheet?periodStart=${periods[0]!.periodStart}`}
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          Open timesheet
        </Link>
      }
    />
  );
}

function PreDeadlineBanner({
  preDeadline,
  onDismiss,
}: {
  preDeadline: PreDeadlineStatusDto;
  onDismiss: ((periodStart: string) => void) | null;
}) {
  const sentence = `Your timesheet for ${preDeadline.label} is due by ${formatDayMonthYear(
    preDeadline.deadlineDate,
  )} - log your hours and submit before the deadline.`;
  return (
    <NoticeCard
      accent="warning"
      testId="pre-deadline-timesheet-banner"
      title="Timesheet due soon"
      sentence={sentence}
      actions={
        <>
          <Link
            to={`/timesheet?periodStart=${preDeadline.periodStart}`}
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            Open timesheet
          </Link>
          {onDismiss !== null ? (
            <TextAction
              tone="neutral"
              aria-label="Dismiss reminder"
              onClick={() => onDismiss(preDeadline.periodStart)}
            >
              Dismiss
            </TextAction>
          ) : null}
        </>
      }
    />
  );
}
